import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import {
  CreateRoleAssignmentItem,
  RoleAssignmentAttributes,
  RoleAssignmentItemStore,
  RoleAssignmentSummaryStore,
} from 'src/stores/role-assignments-store';
import { container } from 'src/inversify.config';
import { Spin, Radio, Alert, Divider, Checkbox, Form, App, Modal } from 'antd';
import { nameof } from 'src/core/utils/object';

import UserSelect from 'src/common/components/business/user-select';
import GroupSelect from 'src/common/components/business/group-select';
import RolesSelect from 'src/common/components/business/roles-select';
import ScopeHierarchySelector from 'src/common/components/business/scope-hierarchy-selector';
import { IdentityType, PermissionsConstants, ScopeType, Selector } from 'src/common/types';
import NewItemModal from 'src/core/ui/newitemmodal';
import { useCurrentUserPermissionsFilter } from '../../common/permission-hooks';
import { getCurrentOrg, getSelectorFromScope } from '../../common/utils';
import { useIdentity } from 'src/core/services/authentication';
import {
  GlobalLicenseManagerRoleName,
  LicenseApproverRoleName,
  LicenseManagerRoleName,
  RoleSummaryStore,
  StructmasterUserRoleName,
} from '../../stores/roles-store';
import {
  LicenseCreationAttributesStore,
  LicenseManagerAssignmentAttributes,
  NewLicensCreationAttributes,
  getUnlimitedLicenseAttributes,
} from '../../stores/licensing-store';
import LicenseAttributesComponent from '../licenses/license-attributes/license-attributes';
import { Query } from '../../core/stores/data-store';
import { OrganizationItemStore } from 'src/stores/organizations-store';
import RoleAssignmentAttributesComponent from './role-attributes/role-attributes';
import { Typography } from 'antd';
import moment from 'moment';

const { Text } = Typography;

interface NewRoleAssignmentFormData {
  identityType: IdentityType;
  identity: { value: string; label: string };
  roles: { value: string; label: string }[];
  hierarchy: { scopeId: string; scopeTitle: string; scopeType: ScopeType; parentId: string; path: string[] };
}
const perm = [PermissionsConstants.ManageGlobalLicenses, PermissionsConstants.ManageLicenses];

const NewRoleAssignmentDialog: FC<{ onClose: (response?: CommandResult<CreateRoleAssignmentItem>, addAnother?: boolean) => void }> = (props) => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const { onClose } = props;
  const [form] = Form.useForm<NewRoleAssignmentFormData>();
  const [identityTypeValue, setIdentityTypeValue] = useState<IdentityType>(IdentityType.User);
  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(undefined);
  const [allowedScopeRoot, setAllowedScopeRoot] = useState<string[]>([]);
  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesStoreState = licenseCreationAttributesStore.state;
  const newRoleAssignmentStore = container.get(RoleAssignmentItemStore);
  const newRoleAssignmentState = newRoleAssignmentStore.state;
  const roleAssignmentSummaryStore = container.get(RoleAssignmentSummaryStore);
  const roleAssignmentSummaryState = roleAssignmentSummaryStore.state;
  const [licenseAttributesVisible, setLicenseAttributesVisible] = useState<boolean>(false);
  const [licenseAttributes, setLicenseAttributes] = useState<LicenseManagerAssignmentAttributes>();
  const [roleAttributes, setRoleAttributes] = useState<RoleAssignmentAttributes>();
  const [userPermissions, checkingPermissions] = useCurrentUserPermissionsFilter(identity, getCurrentOrg(identity).id, perm);
  const hasGlobalLicenseManagerPermission = !checkingPermissions && userPermissions.has(PermissionsConstants.ManageGlobalLicenses);
  const [customError, setCustomError] = useState('');
  const [licenseAttributesErrors, setLicenseAttributesErrors] = useState(false);
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const [currentMaxSelector, setCurrentMaxSelector] = useState(Selector.Organization);
  const organizationStore = container.get(OrganizationItemStore);
  const organizationState = organizationStore.state;
  const [sptAbIsSelected, setIsSptAbSelected] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;
  const [expiredRolesWarningMessage, setExpiredRolesWarningMessage] = useState('');
  const [saveRoleWarning, setSaveRoleWarning] = useState(false);

  const expiredRoleAssignments = useRef<{ roleId: string; scopeId: string; assignmentId: string; label: string }[]>([]);
  const { modal } = App.useApp();

  const handleClose = (response?: any) => {
    newRoleAssignmentStore.clearMessages();
    setCustomError('');
    onClose(response, addAnother);
  };

  const getOrganizationSelector = async (organizationId: string | undefined) => {
    if (organizationId) {
      await organizationStore.load(organizationId);
      return organizationState.item.isPartner.value ? Selector.Partners : Selector.Organization;
    } else {
      return Selector.Organization;
    }
  };

  const onNewItem = async () => {
    let item = (await form.validateFields()) as NewRoleAssignmentFormData;
    const newRoles = item.roles.filter((v) => !expiredRoleAssignments.current.some((x) => x.roleId === v.value));

    let isSuccess = true;
    if (newRoles.length > 0) {
      const postData: CreateRoleAssignmentItem = {
        identityId: item.identity.value,
        identityType: item.identityType,
        roles: newRoles.map((r) => ({ roleId: r.value, role: r.label, expirationDate: roleAttributes?.expirationDate })),
        scopeId: item.hierarchy.scopeId,
      };

      isSuccess = isSuccess && (await newRoleAssignmentStore.create(postData))?.isSuccess === true;

      if (isSuccess && !expiredRoleAssignments.current.some((v) => v.label === LicenseManagerRoleName)) {
        await createLicenseAttributes(item.identity.value, postData.scopeId);
      }
    }

    if (expiredRoleAssignments.current.length > 0 && roleAttributes?.expirationDate) {
      const response = await roleAssignmentSummaryStore.updateExpirationInBulk(
        expiredRoleAssignments.current.map((v) => ({
          assignmentId: v.assignmentId,
          scopeId: v.scopeId,
          newExpirationDate: roleAttributes.expirationDate!,
        }))
      );
      isSuccess = isSuccess && response?.isSuccess === true;
      const existingLicenseManagerRole = expiredRoleAssignments.current.find((v) => v.label === LicenseManagerRoleName);
      if (isSuccess && existingLicenseManagerRole) {
        await createLicenseAttributes(item.identity.value, existingLicenseManagerRole.scopeId);
      }
    }

    expiredRoleAssignments.current = [];
    form.resetFields();
    handleClose({ isSuccess });
  };

  const getWarningMessage = async () => {
    let item = (await form.validateFields()) as NewRoleAssignmentFormData;
    if (item) {
      var query = {
        filter: {
          identityId: { value: item.identity.value, type: 'guid' },
          identityType: item.identityType,
          scopeId: { value: item.hierarchy.scopeId, type: 'guid' },
        },
        skip: 0,
      } as Query;
      var selectedRoles = item.roles.map((x) => {
        return { value: x.value, label: x.label };
      });

      await roleAssignmentSummaryStore.load(query);
      var existingRoles = verifyIfUserAlreadyHasSelectedRole(
        roleAssignmentSummaryState.items.value
          .filter((x) => x.scopeId === item.hierarchy.scopeId)
          .map((x) => ({ id: x.roleId, expirationDate: x.expirationDate, scopeId: x.scopeId, assignmentId: x.id })),
        selectedRoles
      );

      const now = moment(new Date());

      if (existingRoles.length > 0 && existingRoles.some((role) => moment(role.expirationDate).isAfter(now))) {
        modal.error({
          content: t(`Selected${item.identityType}AlreadyHasTheSelectedRoles`).replace(
            '{0}',
            existingRoles
              .filter((v) => moment(v.expirationDate).isAfter(now))
              .map((v) => v.label)
              .join(', ')
          ),
        });
        return;
      } else {
        const expiredRoles = existingRoles.filter((v) => moment(v.expirationDate).isBefore(now));
        expiredRoleAssignments.current = expiredRoles.map((v) => ({
          roleId: v.id,
          scopeId: v.scopeId,
          assignmentId: v.assignmentId,
          label: v.label,
        }));
        const expiredRoleString = expiredRoles.map((v) => v.label).join(', ');
        setExpiredRolesWarningMessage(
          expiredRoles.length > 0 ? t('UpdateExpiredRoleWarning').replace('{0}', expiredRoleString).replaceAll('{1}', item.identity.label) : ''
        );
        if (existingRoles.length > 0) {
          setSaveRoleWarning(true);
        } else {
          onNewItem();
        }
      }
    }
  };

  const verifyIfUserAlreadyHasSelectedRole = (
    userRoles: { id: string; expirationDate?: Date; scopeId: string; assignmentId: string }[],
    selectedRoles: { value: string; label: string }[]
  ) => {
    var existingRoles = [];
    for (const selectedRole of selectedRoles) {
      const userRole = userRoles.find((v) => v.id === selectedRole.value);
      if (userRole) {
        existingRoles.push({
          id: userRole.id,
          expirationDate: userRole.expirationDate,
          label: selectedRole.label,
          scopeId: userRole.scopeId,
          assignmentId: userRole.assignmentId,
        });
      }
    }
    return existingRoles;
  };
  const createLicenseAttributes = async (identityId: string, scopeId: string) => {
    if (licenseAttributes) {
      let item: NewLicensCreationAttributes = {
        identityType: identityTypeValue,
        maximumLicenseDueDate: licenseAttributes.maximumLicenseDueDate,
        maxLicensesPeriodInDays: licenseAttributes.maxLicensesPeriodInDays,
        maxNumberOfLicensesPerMonth: licenseAttributes.maxNumberOfLicensesPerMonth,
        allowedDevices: licenseAttributes.allowedDevices,
        identityId,
        scopeId,
        needsSignOff: licenseAttributes.needsSignOff,
        canCreateRecurrentLicenses: licenseAttributes.canCreateRecurrentLicenses ?? false,
        canCreateUnlimitedLicense: licenseAttributes.canCreateUnlimitedLicense,
      };
      if (item.canCreateUnlimitedLicense) {
        item.maximumLicenseDueDate = unlimitedLicenseDefaults.untilDate;
        item.maxLicensesPeriodInDays = unlimitedLicenseDefaults.maxPeriodInDays;
      }

      const expiredLicenseManagerRole = expiredRoleAssignments.current.find((v) => v.label === LicenseManagerRoleName);
      if (expiredLicenseManagerRole) {
        await licenseCreationAttributesStore.getByUserScope({
          identityId: identityId,
          identityType: identityTypeValue,
          scopeId: expiredLicenseManagerRole.scopeId,
        });

        if (licenseCreationAttributesStoreState.item.value) {
          await licenseCreationAttributesStore.save(licenseCreationAttributesStoreState.item.value.id, {
            ...licenseCreationAttributesStoreState.item.value,
            ...item,
          });
          return;
        }
      }

      await licenseCreationAttributesStore.post(item);
    }
  };
  const onRolesChange = async (values: { value: string; label: string; selector: Selector }[]) => {
    var licenseMnagerRoleSelected = values.any((x) => x.label === LicenseManagerRoleName);
    if (!licenseAttributesVisible) {
      licenseMnagerRoleSelected && setLicenseAttributesVisible(true);
    } else {
      !licenseMnagerRoleSelected && setLicenseAttributesVisible(false);
      setLicenseAttributesErrors(false);
    }
  };
  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes) => {
    setLicenseAttributes(item);
  };

  const onRoleAttributesChange = (item: RoleAssignmentAttributes) => {
    setRoleAttributes(item);
  };

  return (
    <NewItemModal
      width={600}
      className="figma-modal"
      title={t('NewRolesAssignment')}
      onNewItem={getWarningMessage}
      onClose={handleClose}
      onOkButtonIsDisabled={newRoleAssignmentState.isBusy.get() || licenseCreationAttributesStoreState.isBusy.get() || licenseAttributesErrors}
      onCancelButtonIsDisabled={newRoleAssignmentState.isBusy.get() || licenseCreationAttributesStoreState.isBusy.get()}
      closable={!newRoleAssignmentState.isBusy.get()}
      maskClosable={!newRoleAssignmentState.isBusy.get()}
      okText={t('Confirm')}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          {(newRoleAssignmentState.errorMessage.value || '').length !== 0 && (
            <Alert type="error" message={newRoleAssignmentState.errorMessage.value} closable onClose={() => newRoleAssignmentStore.clearMessages()} />
          )}

          <Spin
            spinning={
              newRoleAssignmentState.isBusy.get() ||
              checkingPermissions ||
              licenseCreationAttributesStoreState.isBusy.get() ||
              roleAssignmentSummaryState.isBusy.get() ||
              rolesState.isBusy.get()
            }
          >
            <Form size="middle" form={form} layout="vertical" autoComplete="off" initialValues={{ identityType: identityTypeValue, roles: [] }}>
              <Form.Item label={t('ChooseIdentity')} required>
                <Form.Item name={nameof<NewRoleAssignmentFormData>('identityType')} rules={[{ required: true }]}>
                  <Radio.Group
                    onChange={(e) => {
                      setIdentityTypeValue(e?.target?.value ?? 'User');
                      setAllowedScopeRoot([]);
                      form.setFieldsValue({ ...form.getFieldsValue(), identity: undefined });
                    }}
                  >
                    <Radio value="User">{t('User')}</Radio>
                    <Radio value="Group">{t('Group')}</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item noStyle>
                  {identityTypeValue === 'User' && (
                    <Form.Item noStyle name={nameof<NewRoleAssignmentFormData>('identity')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                      <UserSelect onChange={(_, organizations) => setAllowedScopeRoot(organizations || [])} />
                    </Form.Item>
                  )}
                  {identityTypeValue === 'Group' && (
                    <Form.Item noStyle name={nameof<NewRoleAssignmentFormData>('identity')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                      <GroupSelect onChange={(_, organization) => setAllowedScopeRoot(organization ? [organization] : [])} />
                    </Form.Item>
                  )}
                </Form.Item>
              </Form.Item>
              <Form.Item
                name={nameof<NewRoleAssignmentFormData>('hierarchy')}
                rules={[
                  () => ({
                    required: true,
                    validator(_, value) {
                      if ((value?.scopeId || '').length === 0) return Promise.reject(t('PleaseSelect'));
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <ScopeHierarchySelector
                  allowedScopeRoots={allowedScopeRoot}
                  onChange={async (x) => {
                    form.setFieldsValue({ ...form.getFieldsValue(), roles: [] });
                    const orgId = (x?.path || []).length < 2 ? undefined : x.path[1];
                    setSelectedOrg(orgId);
                    let currentSelector: Selector;
                    if (x.lastScope === ScopeType.Organization) {
                      if (x.scopeTitle.toLowerCase() === 'sptab') {
                        setIsSptAbSelected(true);
                      } else {
                        setIsSptAbSelected(false);
                      }
                      currentSelector = await getOrganizationSelector(orgId);
                    } else {
                      currentSelector = getSelectorFromScope(x.lastScope ?? ScopeType.Organization);
                    }
                    setCurrentMaxSelector(currentSelector);
                  }}
                />
              </Form.Item>

              {identityTypeValue === IdentityType.User && (
                <Form.Item name={nameof<NewRoleAssignmentFormData>('roles')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                  <RolesSelect
                    maxSelector={currentMaxSelector}
                    organizationId={selectedOrg}
                    onChange={onRolesChange}
                    excludeNames={[
                      !hasGlobalLicenseManagerPermission ? LicenseManagerRoleName : '',
                      !hasGlobalLicenseManagerPermission ? LicenseApproverRoleName : '',
                      !sptAbIsSelected ? GlobalLicenseManagerRoleName : '',
                      StructmasterUserRoleName,
                    ]}
                  />
                </Form.Item>
              )}
              {identityTypeValue === IdentityType.Group && (
                <Form.Item name={nameof<NewRoleAssignmentFormData>('roles')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                  <RolesSelect
                    maxSelector={currentMaxSelector}
                    organizationId={selectedOrg}
                    onChange={onRolesChange}
                    excludeNames={[LicenseManagerRoleName, !sptAbIsSelected ? GlobalLicenseManagerRoleName : '', StructmasterUserRoleName]}
                  />
                </Form.Item>
              )}
              <Text style={{ marginBottom: 10 }} strong>
                {t('RoleAssignmentAttributes')}:
              </Text>
              <RoleAssignmentAttributesComponent onChange={onRoleAttributesChange} />
              {licenseAttributesVisible && (
                <>
                  <Divider />
                  <Text style={{ marginBottom: 10 }} strong>
                    {t('LicenseManagerRoleAttributes')}:
                  </Text>
                  <LicenseAttributesComponent
                    onChange={onLicenseAttributesChange}
                    onFormValidation={(errors) => setLicenseAttributesErrors(errors)}
                  />
                </>
              )}
            </Form>

            <Checkbox value={addAnother} onChange={(v) => setAddAnother(v.target.checked)} style={{ marginBottom: 20 }}>
              {t('AddAnotherAssignment')}
            </Checkbox>

            {saveRoleWarning && expiredRolesWarningMessage && (
              <Modal
                title={<h3>{`${t('Confirmation')}`}</h3>}
                open={saveRoleWarning}
                onOk={() => {
                  setSaveRoleWarning(false);
                  onNewItem();
                }}
                onCancel={() => handleClose()}
                okText={t('OK')}
                okButtonProps={{
                  style: { width: '45%', float: 'right' },
                  disabled: licenseCreationAttributesStoreState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
                }}
                cancelText={t('Cancel')}
                cancelButtonProps={{
                  type: 'default',
                  style: { width: '45%' },
                  disabled: licenseCreationAttributesStoreState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
                }}
              >
                <Spin
                  spinning={newRoleAssignmentState.isBusy.get() || checkingPermissions === true || licenseCreationAttributesStoreState.isBusy.get()}
                >
                  <p>{expiredRolesWarningMessage}</p>
                  <p>{t('DoYouWantToContinue')}</p>
                </Spin>
              </Modal>
            )}
          </Spin>
        </>
      }
    />
  );
};

export default NewRoleAssignmentDialog;
